import React, { useState } from "react";
import { Alert, Button, Chip, Stack, Typography } from "@mui/joy";
import { useLocation } from "react-router-dom";
import { useGetIntegrationOAuthAuthorizationUrl } from "../../services/api";

interface OAuthConnectButtonProps {
  integrationKey: string;
  organizationIntegrationId?: string | null;
  workspaceId?: string | null;
  connection?: {
    connected: boolean;
    scopes?: string[];
    expires_at?: string | null;
    status?: string | null;
  } | null;
  connectLabel?: string;
  connectedLabel?: string;
  onError?: (err: Error) => void;
}

const toError = (err: unknown): Error =>
  err instanceof Error ? err : new Error("Failed to start OAuth connect flow.");

const OAuthConnectButton: React.FC<OAuthConnectButtonProps> = ({
  integrationKey,
  organizationIntegrationId,
  workspaceId,
  connection,
  connectLabel = "Connect via OAuth",
  connectedLabel = "Connected",
  onError,
}) => {
  const location = useLocation();
  const getIntegrationOAuthAuthorizationUrl =
    useGetIntegrationOAuthAuthorizationUrl();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const scopes = connection?.scopes || [];

  if (connection?.connected === true) {
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Chip size="sm" color="success" variant="soft">
          {connectedLabel}
        </Chip>
        {scopes.length > 0 && (
          <Typography level="body-xs" color="neutral">
            {scopes.length} {scopes.length === 1 ? "scope" : "scopes"}
          </Typography>
        )}
        {/* TODO Phase 6: revoke */}
      </Stack>
    );
  }

  const handleConnect = async () => {
    if (!integrationKey || !organizationIntegrationId) return;

    setLoading(true);
    setError(null);

    const options: {
      workspaceId?: string;
      orgIntegrationId?: string;
      returnTo?: string;
    } = {
      returnTo: `${location.pathname}${location.search}`,
    };
    if (workspaceId) {
      options.workspaceId = workspaceId;
    }
    if (organizationIntegrationId) {
      options.orgIntegrationId = organizationIntegrationId;
    }

    try {
      const response = await getIntegrationOAuthAuthorizationUrl(
        integrationKey,
        options,
      );
      window.location.assign(response.authorization_url);
    } catch (err: unknown) {
      const normalizedError = toError(err);
      setError(normalizedError.message);
      setLoading(false);
      onError?.(normalizedError);
    }
  };

  return (
    <Stack spacing={0.75} alignItems="flex-start">
      <Button
        size="sm"
        variant="soft"
        onClick={handleConnect}
        loading={loading}
        disabled={!integrationKey || !organizationIntegrationId}
      >
        {connectLabel}
      </Button>
      {error && (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      )}
    </Stack>
  );
};

export default OAuthConnectButton;
